/**
 * usePromotions — fetches active promotions for the current branch.
 *
 * Calls `promotionsApi.list(branchId)` once `useBranch()` resolves a branchId.
 * Returns the full promotion list plus a derived `visiblePromotions` list
 * filtered to currently-active, auto-applied promotions suitable for surfacing
 * on the Home screen "Special offers" card.
 *
 * The signed-in customer's identity is sent with the request so the service can
 * drop promos they have already exhausted (`maxUsagePerCustomer`). Without it a
 * "first order only" offer kept showing in the cart and was then stripped at
 * checkout — the discount the customer was promised silently disappeared.
 */

import { useEffect, useState } from "react";

import { isAutoAppliedLikeTill, promotionsApi } from "@/api/services";
import { isPromotionWithinWindow } from "@/lib/promotion-window";
import { useBranch } from "@/store/branch";
import { useFulfillment } from "@/store/fulfillment";
import { useAddresses } from "@/hooks/useAddresses";
import { useAuth } from "@/store/auth";
import { localize } from "@/hooks/useMenu";
import { PromotionStatus } from "@/api/online-ordering-service/model";
import type { Promotion } from "@/api/online-ordering-service/model";

export type UsePromotionsResult = {
  promotions: Promotion[];
  visiblePromotions: Promotion[];
  loading: boolean;
  error: Error | null;
};

/**
 * The home "Special offers" card shows a promotion only if the operator
 * allowed the "home" surface; [] hides it everywhere. A promotion saved before
 * surfaces existed has no field at all and keeps showing, as it always did, so
 * no data migration is needed. This only gates the card: `promotions` (cart
 * auto-apply, promo codes) is unaffected.
 */
export function isShownOnHome(p: Promotion): boolean {
  return p.surfaces == null || p.surfaces.includes("home");
}

export function isVisible(p: Promotion): boolean {
  if (!isShownOnHome(p)) return false;
  // Status must be active (or unset, treated as active).
  if (p.status && p.status !== PromotionStatus.Active) return false;
  // Show all active promotions on the home card, even if they require a promo code.
  // Within validity window.
  const now = new Date();
  const start = p.validity?.startDate ? new Date(p.validity.startDate) : null;
  const end = p.validity?.endDate ? new Date(p.validity.endDate) : null;
  if (start && now < start) return false;
  if (end && now > end) return false;
  return true;
}

/**
 * The identity the promotions endpoints use to enforce per-customer usage
 * limits. `customerId` is the reliable key; the phone is a fallback for records
 * that predate it. Both undefined when signed out — the service then fails open
 * and order creation refuses a spent promo instead.
 *
 * Every promotions call should pass this, or the customer sees offers they can
 * no longer earn.
 */
export function usePromotionIdentity(): {
  customerId?: string;
  customerPhone?: string;
} {
  const { user } = useAuth();
  return { customerId: user?.id, customerPhone: user?.phone };
}

export function usePromotions(): UsePromotionsResult {
  const { branchId: rawBranchId, branchConfig } = useBranch();
  // Before the config loads, branchId falls back to the URL slug (the menu
  // link id), which is not a branch: wait for the config.
  const branchId = branchConfig ? rawBranchId : null;
  const { mode, selectedAddressId, manualAddress } = useFulfillment();
  const { addresses } = useAddresses();
  const { customerId, customerPhone } = usePromotionIdentity();
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Resolve the customer's delivery coordinates (delivery mode only) so the
  // service can enforce geofenced promotions. Pickup / no-address → undefined,
  // and geofenced promos fail closed (excluded) until a location is known.
  const deliveryAddr =
    mode === "delivery"
      ? (manualAddress ??
        (selectedAddressId
          ? addresses.find((a) => a.id === selectedAddressId)
          : null))
      : null;
  const coords = deliveryAddr?.coordinates;
  const lat = coords?.latitude != null ? Number(coords.latitude) : undefined;
  const lng = coords?.longitude != null ? Number(coords.longitude) : undefined;

  useEffect(() => {
    let cancelled = false;
    if (!branchId) {
      setPromotions([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const list = await promotionsApi.list(branchId, {
          // Storefront channel, matching `channel: ChannelEnum.Online` on the
          // create-order payload, so channel-restricted promos are filtered.
          orderSource: "online",
          lat,
          lng,
          customerId,
          customerPhone,
        });
        if (!cancelled) {
          setPromotions(list ?? []);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setPromotions([]);
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Refetch on sign-in/out: the list is identity-scoped, so a cached
    // anonymous list would keep showing a promo this customer has spent.
  }, [branchId, lat, lng, customerId, customerPhone]);

  const visiblePromotions = promotions.filter(isVisible);

  return { promotions, visiblePromotions, loading, error };
}

/** Convenience helpers for screens that render a promotion card. */
export function getPromotionName(p: Promotion, lang: string): string {
  return localize(p.name, lang);
}

export function getPromotionDescription(p: Promotion, lang: string): string {
  return localize(p.description ?? null, lang);
}

export function isPromotionAutoApplied(p: Promotion): boolean {
  return isAutoAppliedLikeTill(p);
}
